import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { supabaseAuthService } from '../services/supabaseAuthService';
import { AuthState, User, LoginCredentials, RegisterCredentials } from '../types/auth';

// Tipos para el contexto
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  generateWalletAddress: () => string;
  generateSeedPhrase: () => string;
}

// Estado inicial
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Tipos de acciones
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean };

// Reducer para manejar el estado
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

// Crear el contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook personalizado para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// Props del provider
interface AuthProviderProps {
  children: ReactNode;
}

// Provider del contexto
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Verificar autenticación al cargar la app
  useEffect(() => {
    let isMounted = true;
    
    const checkAuth = async () => {
      try {
        // Verificar si hay un token almacenado
        const isAuth = await supabaseAuthService.isAuthenticated();
        if (isAuth) {
          const storedUser = supabaseAuthService.getStoredUser();
          if (storedUser && isMounted) {
            dispatch({ type: 'AUTH_SUCCESS', payload: storedUser });
          } else if (isMounted) {
            // Intentar obtener usuario del servidor
            const response = await supabaseAuthService.getCurrentUser();
            if (response.success && response.data && isMounted) {
              dispatch({ type: 'AUTH_SUCCESS', payload: response.data });
            } else if (isMounted) {
              // Token inválido, limpiar
              await supabaseAuthService.logout();
              dispatch({ type: 'AUTH_LOGOUT' });
            }
          }
        } else if (isMounted) {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        if (isMounted) {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    };

    checkAuth();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Función de login
  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await supabaseAuthService.login(credentials);
      
      if (response.success && response.data) {
        dispatch({ type: 'AUTH_SUCCESS', payload: response.data.user });
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: response.error || 'Error en el login' });
      }
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: 'Error en el login' });
    }
  };

  // Función de registro
  const register = async (credentials: RegisterCredentials) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await supabaseAuthService.register(credentials);
      
      if (response.success && response.data) {
        dispatch({ type: 'AUTH_SUCCESS', payload: response.data.user });
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: response.error || 'Error en el registro' });
      }
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: 'Error en el registro' });
    }
  };

  // Función de logout
  const logout = async () => {
    try {
      await supabaseAuthService.logout();
      dispatch({ type: 'AUTH_LOGOUT' });
    } catch (error) {
      console.error('Error en logout:', error);
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  // Función para limpiar errores
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Funciones para generar wallet
  const generateWalletAddress = () => {
    return supabaseAuthService.generateWalletAddress();
  };

  const generateSeedPhrase = () => {
    return supabaseAuthService.generateSeedPhrase();
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    clearError,
    generateWalletAddress,
    generateSeedPhrase,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 